import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Heart, Shield, Star, Truck, Eye, Play, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { useState, useRef } from "react";

export const ProductCard = ({ product }: { product: any }) => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const wishlisted = isInWishlist(product.id);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const getVideoInfo = (url: string) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i);
    if (ytMatch) return { type: 'youtube', id: ytMatch[1], thumb: `https://img.youtube.com/vi/${ytMatch[1]}/maxresdefault.jpg` };
    const vimeoMatch = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/i);
    if (vimeoMatch) return { type: 'vimeo', id: vimeoMatch[1] };
    return { type: 'direct', url };
  };

  const togglePlayback = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const videoInfo = getVideoInfo(product.video_url);
    if (videoInfo?.type === 'youtube' || videoInfo?.type === 'vimeo') {
      // For external links, we just navigate or open modal if implemented
      // For now, let the user click the card to go to details
      return;
    }

    if (isPlaying) {
      videoRef.current?.pause();
      setIsPlaying(false);
    } else {
      videoRef.current?.play().catch(console.error);
      setIsPlaying(true);
    }
  };

  const formatPrice = (price: number) => `${price.toLocaleString()} DH`;

  // Translate badge text using i18n lookup map
  const BADGE_KEY_MAP: Record<string, string> = {
    "certified": "product.badgeCertified",
    "like new": "product.badgeLikeNew",
    "refurbished": "product.badgeRefurbished",
    "featured": "product.badgeFeatured",
    "new": "product.badgeNew",
    "best seller": "product.badgeBestSeller",
    "bestseller": "product.badgeBestSeller",
  };
  const translateBadge = (badge: string) => {
    const key = BADGE_KEY_MAP[badge.toLowerCase().trim()];
    return key ? t(key) : badge;
  };

  const discount = product.original_price && product.original_price > product.price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  const isOutOfStock = product.stock_quantity <= 0;
  const isLowStock = product.stock_quantity > 0 && product.stock_quantity <= 5;

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`group h-full bg-card rounded-2xl overflow-hidden border transition-all duration-300 ${
        isOutOfStock
          ? "border-border/30 opacity-75"
          : "border-border/50 hover:border-primary/30 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_60px_-15px_hsl(var(--primary)/0.15)]"
      }`}>
        {/* Image area */}
        <Link to={`/product/${product.slug}`} className="block relative overflow-hidden">
          <div className="aspect-[5/4.5] bg-[#F9FAFB] flex items-center justify-center relative">
            {!imageLoaded && (
              <div className="absolute inset-0 bg-muted/10 animate-pulse" />
            )}
            <div className="absolute inset-0 z-0">
              <AnimatePresence>
               {isHovered && product.video_url && !isOutOfStock ? (
                  <motion.div
                    key="video-preview"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 z-10 bg-white"
                  >
                    {(() => {
                      const videoInfo = getVideoInfo(product.video_url);
                      if (videoInfo?.type === 'youtube' || videoInfo?.type === 'vimeo') {
                        return (
                          <div className="relative w-full h-full">
                            <img 
                              src={videoInfo.type === 'youtube' ? videoInfo.thumb : "/placeholder.svg"} 
                              className="w-full h-full object-cover" 
                              alt="Video Preview"
                            />
                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                              <div className="w-12 h-12 rounded-full bg-red-600/90 flex items-center justify-center shadow-xl transform scale-110">
                                <Play className="w-5 h-5 text-white fill-current" />
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <>
                          <video
                            ref={videoRef}
                            src={product.video_url}
                            className="w-full h-full object-cover"
                            muted
                            loop
                            playsInline
                            preload="none"
                          />
                          {!isPlaying && (
                            <button 
                              onClick={togglePlayback}
                              className="absolute inset-0 z-20 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-colors group/play"
                            >
                              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center transform group-hover/play:scale-110 transition-transform">
                                <Play className="w-6 h-6 text-white fill-white/80" />
                              </div>
                            </button>
                          )}
                          {isPlaying && (
                            <button 
                              onClick={togglePlayback}
                              className="absolute bottom-2 left-2 z-20 p-1.5 rounded-lg bg-black/40 backdrop-blur-md text-white border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3" />
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </motion.div>
                ) : (
                  <motion.img
                    key="static-image"
                    src={product.image_url || "/placeholder.svg"}
                    alt={product.name}
                    width={400}
                    height={360}
                    className={`w-full h-full object-contain p-4 transition-all duration-700 ease-out drop-shadow-sm ${
                      imageLoaded ? "opacity-100" : "opacity-0"
                    } ${isHovered && !isOutOfStock ? "scale-105" : "scale-100"}`}
                    onLoad={() => setImageLoaded(true)}
                    loading="lazy"
                  />
                )}
              </AnimatePresence>
            </div>

            {/* Cinematic Video Badge (Floating) */}
            {product.video_url && !isOutOfStock && (
              <div className="absolute bottom-3 right-3 z-20 flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-600/90 backdrop-blur-md text-white text-[9px] font-black uppercase tracking-wider shadow-lg transform group-hover:scale-110 transition-transform">
                <Play className="w-2.5 h-2.5 fill-current" /> Cinematic
              </div>
            )}

            {/* Subtle Gradient Overlays */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none z-10" />

            {/* Out of stock overlay */}
            {isOutOfStock && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-10">
                <span className="px-5 py-2.5 rounded-2xl bg-slate-900 text-white text-[11px] font-black tracking-[0.1em] uppercase shadow-lg">
                  {t('product.soldOut')}
                </span>
              </div>
            )}
            
            {/* Low stock urgency — docked to bottom of image area */}
            {isLowStock && (
              <div className="absolute bottom-0 left-0 right-0 bg-destructive/90 backdrop-blur-md text-destructive-foreground text-center py-1.5 z-10 transform translate-y-0">
                <p className="text-[10px] sm:text-[11px] font-black tracking-widest uppercase">
                  🔥 {t('product.lowStock', { count: product.stock_quantity })}
                </p>
              </div>
            )}
          </div>

          {/* Top-left badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-20">
            {discount > 0 && (
              <span className="px-2.5 py-1 rounded-full text-[10px] sm:text-[11px] font-black bg-destructive text-destructive-foreground shadow-md ring-1 ring-white/10">
                -{discount}%
              </span>
            )}
            {product.badge && (
              <span className="px-2.5 py-1 rounded-full text-[10px] sm:text-[11px] font-black bg-primary text-primary-foreground shadow-md ring-1 ring-white/10">
                {translateBadge(product.badge)}
              </span>
            )}
            {product.condition === "used" && (
              <span className="px-2.5 py-1 rounded-full text-[10px] sm:text-[11px] font-black bg-slate-900 text-white shadow-md ring-1 ring-white/10">
                {t('shop.used')}
              </span>
            )}
          </div>

          {/* Top-right action buttons */}
          <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-20">
            <button
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-xl shadow-lg border border-white/20 ${
                wishlisted
                  ? "bg-destructive text-white scale-110"
                  : "bg-white/70 text-slate-800 hover:bg-white hover:text-destructive sm:opacity-0 sm:group-hover:opacity-100 sm:translate-y-1 sm:group-hover:translate-y-0"
              }`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(product.id); }}
              aria-label={t("common.toggleWishlist")}
            >
              <Heart className={`w-[18px] h-[18px] ${wishlisted ? "fill-current" : ""}`} />
            </button>
            <button
              className="w-10 h-10 rounded-full flex items-center justify-center bg-white/70 text-slate-800 hover:bg-white hover:text-primary border border-white/20 backdrop-blur-xl shadow-lg transition-all duration-300 sm:opacity-0 sm:group-hover:opacity-100 sm:translate-y-2 sm:group-hover:translate-y-0"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              aria-label={t("common.quickView")}
            >
              <Eye className="w-[18px] h-[18px]" />
            </button>
          </div>
        </Link>

        {/* Content area */}
        <div className="p-3.5 sm:p-4 flex flex-col gap-1.5">
          {/* Brand */}
          <p className="text-[10px] sm:text-[11px] text-muted-foreground font-semibold uppercase tracking-[0.15em]">
            {product.brand}
          </p>

          {/* Product name */}
          <Link to={`/product/${product.slug}`} className="block">
            <h3 className="font-semibold text-foreground text-[13px] sm:text-sm leading-snug hover:text-primary transition-colors line-clamp-2 min-h-[2.5rem]">
              {product.name}
            </h3>
          </Link>

          {/* Specs line */}
          {(product.storage || product.condition) && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {product.storage && (
                <span className="text-[10px] sm:text-[11px] text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded-md font-medium">
                  {product.storage}
                </span>
              )}
              {product.condition && (
                <span className="text-[10px] sm:text-[11px] text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded-md font-medium">
                  {product.condition === "used" ? t('shop.used') : t('shop.new')}
                </span>
              )}
              {product.warranty_months && (
                <span className="text-[10px] sm:text-[11px] text-primary/80 bg-primary/8 px-1.5 py-0.5 rounded-md font-medium flex items-center gap-0.5">
                  <Shield className="w-2.5 h-2.5" />
                  {t('product.warrantyShort', { months: product.warranty_months })}
                </span>
              )}
            </div>
          )}

          {/* Price */}
          <div className="flex items-baseline gap-2 pt-1.5 mt-auto">
            <span className="font-display text-lg sm:text-xl font-bold text-primary leading-none">
              {formatPrice(product.price)}
            </span>
            {discount > 0 && (
              <span className="text-[11px] sm:text-xs text-muted-foreground line-through">
                {formatPrice(product.original_price)}
              </span>
            )}
          </div>

          {/* Trust micro-strip */}
          <div className="flex items-center gap-2.5 pt-1 text-[9px] sm:text-[10px] text-muted-foreground/70">
            <span className="flex items-center gap-0.5">
              <Shield className="w-2.5 h-2.5 text-primary/60" /> {t('product.verified')}
            </span>
            <span className="flex items-center gap-0.5">
              <Truck className="w-2.5 h-2.5 text-primary/60" /> {t('product.deliveryShort')}
            </span>
          </div>

          {/* Add to Cart */}
          <button
            onClick={() => !isOutOfStock && addToCart(product.id)}
            disabled={isOutOfStock}
            className={`w-full flex items-center justify-center gap-2 py-3 sm:py-2.5 mt-2 rounded-full text-sm font-bold transition-all duration-300 ${
              isOutOfStock
                ? "bg-muted text-muted-foreground cursor-not-allowed"
                : "bg-primary text-primary-foreground hover:bg-primary/95 active:scale-[0.98] shadow-[0_8px_20px_-4px_rgba(var(--primary-rgb),0.3)] hover:shadow-[0_12px_25px_-4px_rgba(var(--primary-rgb),0.5)]"
            }`}
          >
            <ShoppingCart className="w-4 h-4" />
            {isOutOfStock ? t('product.soldOut') : t('product.addToCart')}
          </button>
        </div>
      </div>
    </motion.div>
  );
};
